import React, { useState, useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, RadarChart, Radar, PolarGrid, PolarAngleAxis } from 'recharts';
import { C, St, FONT_BODY, FONT_MONO } from '../theme';
import { calcVolume, calcLoadProjection } from '../science';

// ─── EXERCISE HISTORY ────────────────────────────────────────────────────────
export const RANGES=[{id:"1m",label:"1M"},{id:"3m",label:"3M"},{id:"6m",label:"6M"},{id:"all",label:"Tutto"}];

export function CombinedChartTip({active,payload,label}) {
  if(!active||!payload?.length)return null;
  const items=[
    {key:"peso", label:"Carico", unit:" kg", color:C.cyan},
    {key:"rm1",  label:"1RM",   unit:" kg", color:C.blue},
    {key:"reps", label:"Reps",  unit:" reps",color:C.purple},
  ];
  return(
    <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"8px 12px",fontSize:11,lineHeight:1.7}}>
      <div style={{color:C.muted,marginBottom:2,fontSize:10}}>{label}</div>
      {items.map(it=>{
        const p=payload.find(x=>x.dataKey===it.key);
        if(!p||p.value==null)return null;
        return<div key={it.key} style={{color:it.color,fontWeight:700}}>{it.label}: {p.value}{it.unit}</div>;
      })}
    </div>
  );
}
const MULTI_LINES = [
  { id: "volume",    label: "Volume",    color: C.green,  stroke: 2,   dash: undefined, unit: " kg·r", yAxis: "vol" },
  { id: "repsTotal", label: "Reps tot.", color: C.purple, stroke: 2,   dash: "5 3",     unit: " reps", yAxis: "reps" },
  { id: "peso",      label: "Carico",    color: C.cyan,   stroke: 2.5, dash: undefined, unit: " kg",   yAxis: "kg" },
  { id: "rm1",       label: "1RM",       color: C.blue,   stroke: 2,   dash: "4 2",     unit: " kg",   yAxis: "kg" },
];

function MultiLineTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 12px", fontSize: 11, lineHeight: 1.7 }}>
      <div style={{ color: C.muted, marginBottom: 2, fontSize: 10 }}>{label}</div>
      {MULTI_LINES.map(ln => {
        const p = payload.find(x => x.dataKey === ln.id);
        if (!p || p.value == null) return null;
        return <div key={ln.id} style={{ color: ln.color, fontWeight: 700 }}>{ln.label}: {p.value}{ln.unit}</div>;
      })}
    </div>
  );
}

export function ExerciseCharts({ cd, tRM, tPeso, tVol, tReps, tRpe, hideRPE, multiMode }) {
  const hasPeso = cd.some(d => d.peso);
  const hasReps = cd.some(d => d.reps);

  // Multi-line mode (used by CoachChatPage)
  const defaultChecked = { volume: true, repsTotal: true, peso: true, rm1: false };
  const [checked, setChecked] = useState(defaultChecked);

  // Single-tab mode (legacy, used by HistoryPage etc.)
  const tabs = [
    { id: "rm1",    label: "1RM",    color: C.blue,   trend: tRM },
    { id: "peso",   label: "Carico", color: C.cyan,   trend: tPeso,  cond: hasPeso },
    { id: "volume", label: "Volume", color: C.green,  trend: tVol },
    { id: "reps",   label: "Reps",   color: C.purple, trend: tReps,  cond: hasReps },
    ...(!hideRPE ? [
      { id: "rpe",   label: "RPE",  color: C.red,   trend: tRpe, cond: cd.some(d => d.rpe) },
      { id: "misto", label: "Misto",color: C.text,  trend: null, cond: hasPeso && hasReps },
    ] : []),
  ].filter(t => t.cond !== false);
  const [active, setActive] = useState(tabs[0]?.id || "rm1");
  const [repsMode, setRepsMode] = useState("media");
  const tab = tabs.find(t => t.id === active) || tabs[0];

  if (multiMode) {
    const activeLines = MULTI_LINES.filter(ln => checked[ln.id] && cd.some(d => d[ln.id] != null));
    const hasVol = activeLines.some(l => l.yAxis === "vol");
    const hasKg = activeLines.some(l => l.yAxis === "kg");
    const hasRepsAxis = activeLines.some(l => l.yAxis === "reps");
    return (
      <div>
        {/* Checkbox row */}
        <div style={{ display: "flex", gap: 14, marginBottom: 12, flexWrap: "wrap" }}>
          {MULTI_LINES.map(ln => {
            const hasData = cd.some(d => d[ln.id] != null);
            if (!hasData) return null;
            const on = checked[ln.id];
            return (
              <label key={ln.id} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", userSelect: "none" }}>
                <div
                  onClick={() => setChecked(prev => ({ ...prev, [ln.id]: !prev[ln.id] }))}
                  style={{
                    width: 16, height: 16, borderRadius: 4,
                    background: on ? ln.color : "transparent",
                    border: `2px solid ${on ? ln.color : C.fill}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.15s", flexShrink: 0,
                  }}>
                  {on && <svg width={10} height={8} viewBox="0 0 10 8"><polyline points="1,4 4,7 9,1" fill="none" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                <span style={{ fontSize: 12, fontWeight: 500, color: on ? C.text : C.subtle }}>
                  {ln.label}
                  {ln.id === "repsTotal" && checked.repsTotal && (
                    <span style={{ marginLeft: 4, fontSize: 10, color: C.subtle }}>
                      {/* no extra toggle needed — always totale */}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
        {/* Chart */}
        <ResponsiveContainer width="100%" height={130}>
          <LineChart data={cd} margin={{ top: 4, right: hasRepsAxis ? 28 : 8, left: -22, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: C.subtle }} axisLine={false} tickLine={false} />
            {(hasVol || hasKg) && <YAxis yAxisId="left" tick={{ fontSize: 9, fill: C.subtle }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />}
            {hasRepsAxis && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9, fill: C.purple }} axisLine={false} tickLine={false} domain={[0, "auto"]} width={24} />}
            <Tooltip content={<MultiLineTip />} />
            {activeLines.map(ln => (
              <Line
                key={ln.id}
                yAxisId={ln.yAxis === "reps" ? "right" : "left"}
                type="monotone"
                dataKey={ln.id}
                stroke={ln.color}
                strokeWidth={ln.stroke}
                strokeDasharray={ln.dash}
                dot={{ r: 2.5, fill: ln.color }}
                connectNulls
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        {/* Legend */}
        <div style={{ display: "flex", gap: 14, marginTop: 8, paddingLeft: 2 }}>
          {activeLines.map(ln => (
            <div key={ln.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <svg width={18} height={8}>
                <line x1={0} y1={4} x2={18} y2={4} stroke={ln.color} strokeWidth={ln.stroke} strokeDasharray={ln.dash} />
              </svg>
              <span style={{ fontSize: 10, color: C.subtle }}>{ln.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Legacy single-tab mode
  if (!tab) return null;
  const dataKey = tab.id === "reps" ? (repsMode === "totale" ? "repsTotal" : "reps") : tab.id;
  const repsUnit = repsMode === "totale" ? " reps totali" : " reps";
  return (
    <div style={{ ...St.card, marginBottom: 10 }}>
      <div style={{ display: "flex", background: C.fill, borderRadius: 9, padding: 2, marginBottom: 14, gap: 0 }}>
        {tabs.map(t => {
          const sel = active === t.id;
          return (
            <button key={t.id} onClick={() => setActive(t.id)}
              style={{ flex: 1, padding: "6px 4px", border: "none", borderRadius: 7,
                background: sel ? "#fff" : "transparent", color: sel ? C.text : C.subtle,
                fontSize: 11, fontWeight: sel ? 600 : 400, cursor: "pointer", fontFamily: FONT_BODY,
                whiteSpace: "nowrap", boxShadow: sel ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                transition: "all 0.15s", minWidth: 0 }}>
              {t.label}
            </button>
          );
        })}
      </div>
      {tab.id === "reps" && (
        <div style={{ display: "flex", background: C.fill, borderRadius: 7, padding: 2, marginBottom: 12, gap: 0, width: "fit-content" }}>
          {[{ id: "media", label: "Media" }, { id: "totale", label: "Totale" }].map(opt => {
            const sel = repsMode === opt.id;
            return (
              <button key={opt.id} onClick={() => setRepsMode(opt.id)}
                style={{ padding: "4px 12px", border: "none", borderRadius: 5,
                  background: sel ? "#fff" : "transparent", color: sel ? C.text : C.subtle,
                  fontSize: 11, fontWeight: sel ? 600 : 400, cursor: "pointer", fontFamily: FONT_BODY,
                  boxShadow: sel ? "0 1px 3px rgba(0,0,0,0.12)" : "none", transition: "all 0.15s" }}>
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
      {tab.id === "misto" ? (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.subtle, marginBottom: 10 }}>Carico · 1RM · Reps</div>
          <ResponsiveContainer width="100%" height={130}>
            <LineChart data={cd} margin={{ top: 4, right: 30, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: C.subtle }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="kg" tick={{ fontSize: 9, fill: C.subtle }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
              <YAxis yAxisId="reps" orientation="right" tick={{ fontSize: 9, fill: C.purple }} axisLine={false} tickLine={false} domain={[0, "auto"]} width={22} />
              <Tooltip content={<CombinedChartTip />} />
              <Line yAxisId="kg"   type="monotone" dataKey="peso" stroke={C.cyan}   strokeWidth={2} dot={{ r: 2, fill: C.cyan }}   connectNulls activeDot={{ r: 4 }} />
              <Line yAxisId="kg"   type="monotone" dataKey="rm1"  stroke={C.blue}   strokeWidth={2} dot={{ r: 2, fill: C.blue }}   connectNulls activeDot={{ r: 4 }} strokeDasharray="4 2" />
              <Line yAxisId="reps" type="monotone" dataKey="reps" stroke={C.purple} strokeWidth={2} dot={{ r: 2, fill: C.purple }} connectNulls activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", gap: 14, marginTop: 8, paddingLeft: 4 }}>
            {[{ c: C.cyan, l: "Carico" }, { c: C.blue, l: "1RM", dash: true }, { c: C.purple, l: "Reps" }].map(({ c, l, dash }) => (
              <div key={l} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <svg width={18} height={8}><line x1={0} y1={4} x2={18} y2={4} stroke={c} strokeWidth={2} strokeDasharray={dash ? "4 2" : undefined} /></svg>
                <span style={{ fontSize: 10, color: C.subtle }}>{l}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={St.secTit}>{
              tab.id === "rm1" ? "1RM stimato" :
              tab.id === "peso" ? "Carico medio" :
              tab.id === "volume" ? "Volume" :
              tab.id === "reps" ? (repsMode === "totale" ? "Reps totali" : "Reps (media)") : "RPE"
            }</div>
            {tab.trend && <span style={{ fontSize: 11, fontWeight: 600, color: tab.trend.color }}>{tab.trend.text}</span>}
          </div>
          {tab.id === "volume"
            ? <ResponsiveContainer width="100%" height={110}><BarChart data={cd} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: C.subtle }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: C.subtle }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip unit=" kg·r" />} />
                <Bar dataKey="volume" fill={C.green} radius={[4, 4, 0, 0]} />
              </BarChart></ResponsiveContainer>
            : <ResponsiveContainer width="100%" height={110}><LineChart data={cd} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: C.subtle }} axisLine={false} tickLine={false} />
                <YAxis domain={tab.id === "rpe" ? [6, 10] : ["auto", "auto"]} tick={{ fontSize: 10, fill: C.subtle }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip unit={tab.id === "reps" ? repsUnit : tab.id === "rpe" ? "" : " kg"} />} />
                <Line type="monotone" dataKey={dataKey} stroke={tab.color} strokeWidth={2.5} dot={{ r: 3, fill: tab.color }} connectNulls activeDot={{ r: 5 }} />
              </LineChart></ResponsiveContainer>
          }
        </>
      )}
    </div>
  );
}


export function MiniVolumeChart({sessions, colorMap, onDayClick, weekMonday}) {
  const [hovered,setHovered]=useState(null);
  const today=new Date();
  const dow=(today.getDay()+6)%7;
  const monday=weekMonday ? new Date(weekMonday) : new Date(today);
  if(!weekMonday){monday.setDate(today.getDate()-dow);}
  monday.setHours(0,0,0,0);
  const days=["L","M","M","G","V","S","D"].map((label,i)=>{
    const d=new Date(monday);
    d.setDate(monday.getDate()+i);
    const iso=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const daySessions=sessions.filter(s=>s.date===iso);
    const vol=daySessions.reduce((sum,s)=>sum+s.exercises.reduce((a,e)=>a+calcVolume(e.sets),0),0);
    const schedaColor=daySessions[0]&&colorMap?(colorMap[daySessions[0].workoutName]||null):null;
    return{label,vol:Math.round(vol),isToday:d.toDateString()===today.toDateString(),isFuture:d>today,iso,schedaColor,name:daySessions[0]?.workoutName||null};
  });
  const weekHasData=days.some(d=>d.vol>0);

  // Monthly fallback: one bar per month of the current year
  const MONTH_LABELS=["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
  const currentYear=today.getFullYear();
  const months=useMemo(()=>MONTH_LABELS.map((label,mi)=>{
    const vol=sessions.reduce((sum,s)=>{
      const sd=new Date(s.date);
      if(sd.getFullYear()!==currentYear||sd.getMonth()!==mi)return sum;
      return sum+s.exercises.reduce((a,e)=>a+calcVolume(e.sets),0);
    },0);
    const isCurrent=mi===today.getMonth();
    return{label,vol:Math.round(vol),isCurrent};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }),[sessions,currentYear]);

  const MAX_H=76,MIN_H=10;

  if(!weekHasData){
    const maxVol=Math.max(...months.map(m=>m.vol),1);
    return(
      <div onMouseLeave={()=>setHovered(null)}>
        <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",marginBottom:14}}>
          <span style={{fontSize:12,fontWeight:600,color:C.text,fontFamily:FONT_MONO,minWidth:60,textAlign:"right",opacity:hovered!==null?1:0,transition:"opacity 0.15s"}}>
            {hovered!==null?months[hovered].vol.toLocaleString()+" vol":""}
          </span>
        </div>
        <div style={{display:"flex",alignItems:"flex-end",gap:3,height:MAX_H+24}}>
          {months.map((mo,i)=>{
            const barH=mo.vol>0?Math.max(MIN_H,Math.round((mo.vol/maxVol)*MAX_H)):MIN_H;
            const isHov=hovered===i;
            const isAnyHov=hovered!==null;
            let bg;
            if(isHov&&mo.vol>0)bg=C.blue;
            else if(isHov)bg=`${C.text}22`;
            else if(isAnyHov)bg=`${C.text}14`;
            else if(mo.vol>0)bg=mo.isCurrent?C.blue:`${C.text}2E`;
            else bg=`${C.text}14`;
            const labelCol=isHov?C.blue:mo.isCurrent?C.blue:C.subtle;
            return(
              <div key={i}
                style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",height:MAX_H+24,cursor:"default",position:"relative"}}
                onMouseEnter={()=>setHovered(i)}>
                {isHov&&mo.vol>0&&(
                  <div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",background:C.blue,color:"#fff",fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:7,whiteSpace:"nowrap",zIndex:10,pointerEvents:"none"}}>
                    {mo.vol.toLocaleString()}
                  </div>
                )}
                <div style={{width:"100%",height:barH,borderRadius:20,background:bg,transition:"all 0.2s ease",transform:isHov?"scaleX(1.12)":"scaleX(1)",transformOrigin:"bottom"}}/>
                <span style={{fontSize:9,fontWeight:600,color:labelCol,marginTop:7,transition:"color 0.2s"}}>{mo.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const maxVol=Math.max(...days.map(d=>d.vol),1);
  return(
    <div onMouseLeave={()=>setHovered(null)}>
      <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",marginBottom:14}}>
        <span style={{fontSize:12,fontWeight:600,color:C.text,fontFamily:FONT_MONO,minWidth:60,textAlign:"right",opacity:hovered!==null?1:0,transition:"opacity 0.15s"}}>
          {hovered!==null?days[hovered].vol.toLocaleString()+" vol":""}
        </span>
      </div>
      <div style={{display:"flex",alignItems:"flex-end",gap:5,height:MAX_H+24}}>
        {days.map((day,i)=>{
          const barH=day.vol>0?Math.max(MIN_H,Math.round((day.vol/maxVol)*MAX_H)):MIN_H;
          const isHov=hovered===i;
          const isNeighbor=hovered!==null&&(i===hovered-1||i===hovered+1);
          const isAnyHov=hovered!==null;
          const col=day.schedaColor;
          let bg;
          if(isHov&&day.vol>0)bg=col||C.blue;
          else if(isHov)bg=`${C.text}22`;
          else if(isNeighbor&&day.vol>0)bg=`${col||C.blue}55`;
          else if(isAnyHov)bg=`${C.text}14`;
          else if(day.vol>0)bg=col||`${C.text}2E`;
          else bg=`${C.text}14`;
          const labelCol=isHov?(col||C.blue):day.isToday?C.blue:C.subtle;
          return(
            <div key={i}
              style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",height:MAX_H+24,cursor:day.vol>0?"pointer":"default",position:"relative"}}
              onMouseEnter={()=>setHovered(i)}
              onClick={()=>day.vol>0&&onDayClick&&onDayClick(day.iso)}>
              {isHov&&day.vol>0&&(
                <div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",background:col||C.blue,color:"#fff",fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:7,whiteSpace:"nowrap",zIndex:10,pointerEvents:"none",maxWidth:90,textAlign:"center",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {day.name||day.vol.toLocaleString()}
                </div>
              )}
              <div style={{width:"100%",height:barH,borderRadius:20,background:bg,transition:"all 0.2s ease",transform:isHov?"scaleX(1.12)":isNeighbor?"scaleX(1.05)":"scaleX(1)",transformOrigin:"bottom"}}/>
              <span style={{fontSize:10,fontWeight:600,color:labelCol,marginTop:7,transition:"color 0.2s"}}>{day.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
export function ActivityGrid({sessions,colorMap}) {
  const DOT=8,GAP=3,WEEKS=52;

  const dateColorMap=useMemo(()=>{
    const m={};
    sessions.forEach(s=>{if(s.workoutName&&!m[s.date])m[s.date]=colorMap[s.workoutName]||C.blue;});
    return m;
  },[sessions,colorMap]);

  const {weeks,monthLabels}=useMemo(()=>{
    const today=new Date();today.setHours(23,59,59,999);
    const start=new Date(today);
    start.setDate(today.getDate()-WEEKS*7);
    start.setDate(start.getDate()-((start.getDay()+6)%7));
    start.setHours(0,0,0,0);
    const wks=[],labels=[];
    let lastMonth=-1;
    for(let w=0;w<WEEKS;w++){
      const week=[];
      const wd=new Date(start);wd.setDate(start.getDate()+w*7);
      for(let d=0;d<7;d++){
        const dt=new Date(wd);dt.setDate(wd.getDate()+d);
        const iso=`${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
        week.push({iso,color:dateColorMap[iso]||null,isFuture:dt>today});
      }
      wks.push(week);
      const mo=wd.getMonth();
      if(mo!==lastMonth){
        labels.push({col:w,label:wd.toLocaleDateString('it-IT',{month:'short'})});
        lastMonth=mo;
      }
    }
    return{weeks:wks,monthLabels:labels};
  },[dateColorMap]);

  return(
    <div style={{overflowX:'auto',WebkitOverflowScrolling:'touch'}}>
      <div style={{display:'inline-flex',flexDirection:'column',paddingBottom:2}}>
        {/* Month labels */}
        <div style={{position:'relative',height:12,marginLeft:20,marginBottom:4,minWidth:WEEKS*(DOT+GAP)}}>
          {monthLabels.map(({col,label},i)=>(
            <span key={i} style={{position:'absolute',left:col*(DOT+GAP),fontSize:9,fontWeight:600,color:C.subtle,lineHeight:'12px',whiteSpace:'nowrap'}}>{label}</span>
          ))}
        </div>
        <div style={{display:'flex',gap:GAP}}>
          {/* Day labels */}
          <div style={{display:'flex',flexDirection:'column',gap:GAP,width:18,flexShrink:0,alignItems:'flex-end',paddingRight:3}}>
            {['L','','M','','G','','D'].map((d,i)=>(
              <div key={i} style={{height:DOT,fontSize:8,color:C.subtle,fontWeight:500,lineHeight:`${DOT}px`}}>{d}</div>
            ))}
          </div>
          {/* Week columns */}
          {weeks.map((week,wi)=>(
            <div key={wi} style={{display:'flex',flexDirection:'column',gap:GAP,flexShrink:0}}>
              {week.map((day,di)=>(
                <div key={di} style={{
                  width:DOT,height:DOT,borderRadius:'50%',flexShrink:0,
                  background:day.isFuture?'transparent':day.color||`${C.text}22`
                }}/>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ChartTip({active,payload,label,unit=""}) {
  if(!active||!payload?.length)return null;
  return <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"8px 12px",fontSize:12}}><div style={{color:C.muted,marginBottom:2}}>{label}</div>{payload.map((p,i)=><div key={i} style={{color:p.color||C.blue,fontWeight:700}}>{p.value}{unit}</div>)}</div>;
}


// ─── MINI CALENDAR ────────────────────────────────────────────────────────────
export function MiniCalendar({sessions,selectedDate,onSelect}) {
  const [viewDate,setViewDate]=useState(()=>{const d=new Date();return {y:d.getFullYear(),m:d.getMonth()};});
  const {y,m}=viewDate;
  const firstDay=new Date(y,m,1).getDay();
  const daysInMonth=new Date(y,m+1,0).getDate();
  const offset=(firstDay+6)%7;
  const sessionDates=new Set(sessions.map(s=>s.date));
  const months=["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
  const cells=[];
  for(let i=0;i<offset;i++)cells.push(null);
  for(let d=1;d<=daysInMonth;d++)cells.push(d);
  return (
    <div style={{...St.card,padding:"14px 16px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <button onClick={()=>setViewDate(v=>v.m===0?{y:v.y-1,m:11}:{y:v.y,m:v.m-1})}
          style={{background:"none",border:`1px solid ${C.border}`,borderRadius:7,padding:"4px 10px",cursor:"pointer",color:C.muted,fontSize:13}}>‹</button>
        <span style={{fontWeight:700,fontSize:14,color:C.text}}>{months[m]} {y}</span>
        <button onClick={()=>setViewDate(v=>v.m===11?{y:v.y+1,m:0}:{y:v.y,m:v.m+1})}
          style={{background:"none",border:`1px solid ${C.border}`,borderRadius:7,padding:"4px 10px",cursor:"pointer",color:C.muted,fontSize:13}}>›</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:4}}>
        {["L","M","M","G","V","S","D"].map((d,i)=><div key={i} style={{textAlign:"center",fontSize:10,color:C.subtle,fontWeight:600,padding:"2px 0"}}>{d}</div>)}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
        {cells.map((d,i)=>{
          if(!d)return <div key={i}/>;
          const dateStr=`${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
          const hasSess=sessionDates.has(dateStr);
          const isSel=selectedDate===dateStr;
          const isToday=dateStr===new Date().toISOString().split("T")[0];
          return (<button key={i} onClick={()=>onSelect(isSel?null:dateStr)}
            style={{padding:"5px 0",border:"none",borderRadius:7,cursor:hasSess?"pointer":"default",
              background:isSel?C.blue:hasSess?C.blueL:"transparent",
              color:isSel?"#fff":isToday?C.blue:hasSess?C.blue:C.subtle,
              fontWeight:isSel||isToday?700:400,fontSize:12,outline:isToday&&!isSel?`1.5px solid ${C.blueB}`:"none"}}>
            {d}
            {hasSess&&!isSel&&<div style={{width:4,height:4,borderRadius:"50%",background:C.blue,margin:"1px auto 0"}}/>}
          </button>);
        })}
      </div>
      {selectedDate&&<button onClick={()=>onSelect(null)} style={{marginTop:8,background:"none",border:"none",color:C.subtle,fontSize:11,cursor:"pointer",width:"100%"}}>Rimuovi filtro</button>}
    </div>
  );
}

// ─── WEEK STRIP ───────────────────────────────────────────────────────────────
export function WeekStrip({ sessions, selectedDate, onSelect }) {
  const [offset, setOffset] = useState(0);
  const today = useMemo(() => new Date(), []);
  const todayIso = today.toISOString().slice(0, 10);

  const monday = useMemo(() => {
    const d = new Date(today);
    const dow = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - dow + offset * 7);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [today, offset]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().slice(0, 10);
  }), [monday]);

  const sessionDays = useMemo(() => new Set(sessions.map(s => s.date)), [sessions]);

  const monthLabel = monday.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  const DAY_LABELS = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

  const btnStyle = {
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 10px',
    fontSize: 16, color: C.subtle, lineHeight: 1, fontFamily: FONT_BODY,
  };

  return (
    <div style={{ ...St.card, padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.subtle, textTransform: 'capitalize', letterSpacing: 0.1 }}>{monthLabel}</span>
        <div style={{ display: 'flex', gap: 0 }}>
          <button onClick={() => setOffset(o => o - 1)} style={btnStyle}>‹</button>
          {offset !== 0 && <button onClick={() => setOffset(0)} style={{ ...btnStyle, fontSize: 11, padding: '4px 6px', color: C.blue }}>Oggi</button>}
          <button onClick={() => setOffset(o => o + 1)} style={btnStyle}>›</button>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {days.map((iso, i) => {
          const isToday = iso === todayIso;
          const hasSess = sessionDays.has(iso);
          const isSel = selectedDate === iso;
          const dayNum = parseInt(iso.slice(8));
          return (
            <div key={iso} onClick={() => hasSess && onSelect(isSel ? null : iso)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: hasSess ? 'pointer' : 'default' }}>
              <span style={{ fontSize: 11, fontWeight: 500, color: isToday ? C.blue : C.subtle }}>{DAY_LABELS[i]}</span>
              <div style={{
                width: 34, height: 34, borderRadius: 17, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isSel ? C.blue : isToday ? `${C.blue}14` : 'transparent',
                outline: isToday && !isSel ? `1.5px solid ${C.blue}` : 'none',
                outlineOffset: '-1.5px',
              }}>
                <span style={{ fontSize: 15, fontWeight: isToday || isSel ? 700 : 400, color: isSel ? '#fff' : isToday ? C.blue : C.text, fontFamily: FONT_BODY }}>
                  {dayNum}
                </span>
              </div>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: hasSess ? (isSel ? C.blue : `${C.blue}80`) : 'transparent' }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── SPIDER CHART ─────────────────────────────────────────────────────────────
// ─── LOAD PROJECTION CHART ───────────────────────────────────────────────────
const PROJECTION_HORIZONS = [
  { label: 'Pross. sessioni', steps: 4 },
  { label: '3 mesi', steps: 24 },
  { label: '6 mesi', steps: 48 },
  { label: '1 anno', steps: 96 },
];

export function LoadProjectionChart({ occurrences }) {
  const [horizonIdx, setHorizonIdx] = useState(0);
  const horizon = PROJECTION_HORIZONS[horizonIdx];

  const projection = useMemo(
    () => occurrences && occurrences.length >= 3 ? calcLoadProjection(occurrences, horizon.steps) : null,
    [occurrences, horizon.steps]
  );

  if (!projection) return null;
  const { historical, projected, slope, r2 } = projection;
  const allData = [...historical, ...projected];

  const trendDir = slope > 0.1 ? 'up' : slope < -0.1 ? 'down' : 'flat';
  const trendColor = trendDir === 'up' ? C.green : trendDir === 'down' ? C.red : C.subtle;
  const trendText = trendDir === 'up' ? `+${slope} kg/ses` : trendDir === 'down' ? `${slope} kg/ses` : 'stabile';

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '0.5px solid rgba(60,60,67,0.08)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: C.subtle }}>Tendenza carico</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: trendColor }}>{trendText}</span>
        </div>
        <span style={{ fontSize: 10, color: C.subtle }}>R² {r2}</span>
      </div>

      {/* Selettore orizzonte */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
        {PROJECTION_HORIZONS.map((h, i) => (
          <button key={h.label} onClick={() => setHorizonIdx(i)} style={{
            padding: '4px 9px', borderRadius: 7, border: 'none', cursor: 'pointer',
            fontSize: 11, fontWeight: horizonIdx === i ? 700 : 500,
            background: horizonIdx === i ? C.cyan : 'rgba(60,60,67,0.07)',
            color: horizonIdx === i ? '#fff' : C.subtle,
            transition: 'background 0.15s, color 0.15s',
          }}>{h.label}</button>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={110}>
        <LineChart data={allData} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 9, fill: C.subtle }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
          <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10, fill: C.subtle }} axisLine={false} tickLine={false} />
          <Tooltip content={<ProjectionTip />} />
          <Line
            type="monotone" dataKey="peso" stroke={C.cyan} strokeWidth={2}
            dot={({ cx, cy, payload }) => payload.isProjection
              ? <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={3} fill="none" stroke={C.cyan} strokeWidth={1.5} strokeDasharray="3 2"/>
              : <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={2.5} fill={C.cyan}/>
            }
            connectNulls activeDot={{ r: 4 }}
          />
          <Line
            type="monotone" dataKey="trend" stroke={`${C.cyan}50`} strokeWidth={1.5}
            strokeDasharray="4 3" dot={false} connectNulls
          />
        </LineChart>
      </ResponsiveContainer>

      <div style={{ fontSize: 10, color: C.subtle, marginTop: 6 }}>
        Regressione lineare — Zatsiorsky &amp; Kraemer 2006
      </div>
    </div>
  );
}

function ProjectionTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 12px', fontSize: 11 }}>
      <div style={{ color: C.muted, marginBottom: 2 }}>{label}</div>
      <div style={{ color: p?.payload?.isProjection ? C.subtle : C.cyan, fontWeight: 700 }}>
        {p?.value} kg{p?.payload?.isProjection ? ' (prev.)' : ''}
      </div>
    </div>
  );
}

export function AnnualVolumeChart({sessions}) {
  const today = new Date();
  const year = today.getFullYear();
  const MONTH_LABELS = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
  const months = useMemo(() => MONTH_LABELS.map((label, mi) => {
    const vol = sessions.reduce((sum, s) => {
      const sd = new Date(s.date);
      if (sd.getFullYear() !== year || sd.getMonth() !== mi) return sum;
      return sum + s.exercises.reduce((a, e) => a + calcVolume(e.sets), 0);
    }, 0);
    return { label, vol: Math.round(vol), isCurrent: mi === today.getMonth() };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [sessions, year]);

  const currentMonthIdx = today.getMonth();
  const pts = months.slice(0, currentMonthIdx + 1).filter(m => m.vol > 0);
  if (pts.length < 2) {
    // fallback: bar chart se dati insufficienti per una linea
    const maxV = Math.max(...months.map(m => m.vol), 1);
    const MAX_H = 64, MIN_H = 6;
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: MAX_H + 20 }}>
        {months.slice(0, currentMonthIdx + 1).map((mo, i) => {
          const barH = mo.vol > 0 ? Math.max(MIN_H, Math.round((mo.vol / maxV) * MAX_H)) : MIN_H;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: MAX_H + 20 }}>
              <div style={{ width: '100%', height: barH, borderRadius: 20, background: mo.isCurrent ? C.blue : `${C.text}2E` }} />
              <span style={{ fontSize: 9, fontWeight: 600, color: mo.isCurrent ? C.blue : C.subtle, marginTop: 5 }}>{mo.label}</span>
            </div>
          );
        })}
      </div>
    );
  }

  const visMonths = months.slice(0, currentMonthIdx + 1);
  const maxV = Math.max(...visMonths.map(m => m.vol), 1);
  const n = visMonths.length;
  const W = 300, SVG_H = 64, PAD_T = 6, PAD_B = 4;
  const plotH = SVG_H - PAD_T - PAD_B;
  const px = i => n === 1 ? W / 2 : (i / (n - 1)) * W;
  const py = v => PAD_T + plotH - (v / maxV) * plotH;
  const polyline = visMonths.map((m, i) => `${px(i).toFixed(2)},${py(m.vol).toFixed(2)}`).join(' ');
  const lastX = px(n - 1), lastY = py(visMonths[n - 1].vol);
  const fillPath = `M ${px(0).toFixed(2)},${py(visMonths[0].vol).toFixed(2)} `
    + visMonths.slice(1).map((m, i) => `L ${px(i+1).toFixed(2)},${py(m.vol).toFixed(2)}`).join(' ')
    + ` L ${px(n-1).toFixed(2)},${SVG_H} L 0,${SVG_H} Z`;

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${SVG_H}`}
        style={{ width: '100%', height: SVG_H, display: 'block', overflow: 'visible' }}>
        <path d={fillPath} fill={`${C.blue}14`} />
        <polyline points={polyline} fill="none" stroke={C.blue} strokeWidth={2}
          strokeLinejoin="round" strokeLinecap="round"/>
        <circle cx={lastX} cy={lastY} r={4} fill={C.blue}/>
        <circle cx={lastX} cy={lastY} r={7} fill={`${C.blue}30`}/>
      </svg>
      <div style={{ display: 'flex', marginTop: 4, paddingBottom: 2 }}>
        {visMonths.map((m, i) => (
          <div key={i} style={{
            flex: 1,
            textAlign: i === 0 ? 'left' : i === n - 1 ? 'right' : 'center',
            fontSize: 9, fontWeight: m.isCurrent ? 700 : 500,
            color: m.isCurrent ? C.blue : C.subtle,
            fontFamily: FONT_BODY,
          }}>
            {m.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SpiderChart({data, compareData}){
  if(!data)return null;
  const merged=data.map((d,i)=>({
    metric:d.metric,
    tu:d.value,
    ...(compareData?{amico:compareData[i]?.value??0}:{}),
  }));
  return(
    <ResponsiveContainer width="100%" height={200}>
      <RadarChart data={merged} margin={{top:10,right:20,bottom:10,left:20}}>
        <PolarGrid stroke={C.border}/>
        <PolarAngleAxis dataKey="metric" tick={{fontSize:11,fill:C.subtle,fontWeight:600}}/>
        <Radar dataKey="tu" stroke={C.blue} fill={C.blue} fillOpacity={0.18} strokeWidth={2}/>
        {compareData&&<Radar dataKey="amico" stroke={C.purple} fill={C.purple} fillOpacity={0.12} strokeWidth={1.5}/>}
      </RadarChart>
    </ResponsiveContainer>
  );
}
